/**
 * Card model + deck construction.
 *
 * A standard UNO deck is 108 cards:
 *   per colour (4): one 0, two each of 1-9, two Skip, two Reverse, two Draw Two  => 25 * 4 = 100
 *   plus 4 Wild and 4 Wild Draw Four                                            => 8
 */

export const CARD_COLORS = ['red', 'yellow', 'green', 'blue'] as const;
export type CardColor = (typeof CARD_COLORS)[number];

/** `wild` is the "colourless" pseudo-colour carried by Wild / Wild Draw Four faces. */
export type CardFaceColor = CardColor | 'wild';

export type CardKind =
  | 'number'
  | 'skip'
  | 'reverse'
  | 'draw2'
  | 'wild'
  | 'wild4';

export interface Card {
  /** Stable per-deck identity. Used by the client to address a specific card in hand. */
  id: string;
  color: CardFaceColor;
  kind: CardKind;
  /** Only present for `kind === 'number'` (0-9). */
  value?: number;
}

export const isWildCard = (card: Card): boolean =>
  card.kind === 'wild' || card.kind === 'wild4';

/** Cards that inflict a draw penalty on the next player. */
export const isDrawCard = (card: Card): boolean =>
  card.kind === 'draw2' || card.kind === 'wild4';

export function drawAmount(card: Card): number {
  if (card.kind === 'draw2') return 2;
  if (card.kind === 'wild4') return 4;
  return 0;
}

/**
 * Human-readable label, also used as the theme lookup key suffix.
 * e.g. `red-7`, `blue-skip`, `wild`, `wild4`.
 */
export function cardKey(card: Card): string {
  if (card.kind === 'wild') return 'wild';
  if (card.kind === 'wild4') return 'wild4';
  if (card.kind === 'number') return `${card.color}-${card.value}`;
  return `${card.color}-${card.kind}`;
}

export function cardLabel(card: Card): string {
  switch (card.kind) {
    case 'number':
      return String(card.value);
    case 'skip':
      return 'Skip';
    case 'reverse':
      return 'Reverse';
    case 'draw2':
      return '+2';
    case 'wild':
      return 'Wild';
    case 'wild4':
      return '+4';
  }
}

/** Builds an ordered (unshuffled) 108-card deck. */
export function buildDeck(): Card[] {
  const deck: Card[] = [];
  let n = 0;
  const push = (color: CardFaceColor, kind: CardKind, value?: number) => {
    deck.push({ id: `c${n++}`, color, kind, ...(value === undefined ? {} : { value }) });
  };

  for (const color of CARD_COLORS) {
    push(color, 'number', 0);
    for (let v = 1; v <= 9; v++) {
      push(color, 'number', v);
      push(color, 'number', v);
    }
    for (const kind of ['skip', 'reverse', 'draw2'] as const) {
      push(color, kind);
      push(color, kind);
    }
  }
  for (let i = 0; i < 4; i++) {
    push('wild', 'wild');
    push('wild', 'wild4');
  }
  return deck;
}

/** Standard UNO scoring, used to rank the losers when a round ends. */
export function cardScore(card: Card): number {
  if (card.kind === 'number') return card.value ?? 0;
  if (card.kind === 'wild' || card.kind === 'wild4') return 50;
  return 20;
}

export function handScore(hand: Card[]): number {
  return hand.reduce((sum, c) => sum + cardScore(c), 0);
}
